import { z } from 'zod'
import { IsoDateTimeSchema, ProviderIdSchema } from '../primitives'
import { route } from '../route-def'
import { PROVIDER_VALIDATION_STATES } from '../domain/document-policy'
import { LocationPolicyPatchSchema, LocationPolicySchema } from '../domain/location-policy'
import { IpEvaluationSchema } from './location'

/**
 * Providers page (P3), public join links (S0 / "link not active") and the P4 recipient list (/privacy).
 * Provider ids are plain strings from the registry, never an enum (the pluggability rule).
 */

export const PROVIDER_FLOWS = ['hosted_redirect', 'own_capture', 'embedded_sdk'] as const
export const ProviderFlowSchema = z.enum(PROVIDER_FLOWS)
export type ProviderFlowName = z.infer<typeof ProviderFlowSchema>

/** `document`: the namespaced identity-document providers (`*-doc`, identity-document spec §3.1). */
export const PROVIDER_KINDS = ['biometric', 'ipintel', 'document'] as const
export const ProviderKindSchema = z.enum(PROVIDER_KINDS)
export type ProviderKind = z.infer<typeof ProviderKindSchema>

export const FACE_NOTICES = ['kept_for_review', 'kept_to_run_service', 'not_kept', 'test_provider_keeps'] as const
export const ERASURE_NOTICES = [
  'provider_deletes_within_30_days',
  'support_request',
  'nothing_kept',
  'test_provider_cannot_delete',
] as const
export const FaceNoticeSchema = z.enum(FACE_NOTICES)
export const ErasureNoticeSchema = z.enum(ERASURE_NOTICES)

export const ProviderSettingsValuesSchema = z.record(z.string(), z.unknown())

/* ------------------------------------------------------------------ admin */

export const AdminBiometricProviderSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  flow: ProviderFlowSchema,
  desktop: z.boolean(),
  reviewMedia: z.boolean(),
  assurance: z.string(),
  /** In the deployment's PROVIDER_APPROVED (the admin UI cannot change it). */
  approved: z.boolean(),
  /** Its secrets are present in the environment. */
  configured: z.boolean(),
  enabled: z.boolean(),
  /** enabled ∧ configured ∧ approved ∧ its own extra precondition. */
  effective: z.boolean(),
  isDefault: z.boolean(),
  /** Why the provider's own precondition (canEnable) refuses, if it does. */
  blockedReason: z.string().nullable(),
  settings: ProviderSettingsValuesSchema,
  joinPath: z.string(),
  hasSetup: z.boolean(),
  /** T23: a test provider (staff only, E12.9): approved by its own environment flag, never the default. */
  testingOnly: z.boolean().default(false),
})
export type AdminBiometricProvider = z.infer<typeof AdminBiometricProviderSchema>

export const AdminIpSourceSchema = z.object({
  id: z.string(),
  offline: z.boolean(),
  geo: z.boolean(),
  risk: z.boolean(),
  /** Offline sources and cloudflare need no approval (they send nothing): always true for them. */
  approved: z.boolean(),
  configured: z.boolean(),
  enabled: z.boolean(),
  effective: z.boolean(),
  lastRefreshAt: IsoDateTimeSchema.nullable(),
})
export type AdminIpSource = z.infer<typeof AdminIpSourceSchema>

/** Identity-document spec §3.2: deployment config (a reviewed change), never an admin toggle. */
export const ProviderValidationSchema = z.enum(PROVIDER_VALIDATION_STATES)

/** A document provider (identity-document spec §3.1): no default flag (the default lives in the document policy). */
export const AdminDocumentProviderSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  enabled: z.boolean(),
  configured: z.boolean(),
  /** In the deployment's PROVIDER_APPROVED. */
  approved: z.boolean(),
  /** enabled ∧ configured ∧ approved. */
  effective: z.boolean(),
  validation: ProviderValidationSchema,
  /* Identity-document spec §13 provider card (Task 30): coverage, webhook / poller health, deletion, retention. */
  /** Countries the provider's coverage (static list or stored catalogue snapshot) includes. */
  coverageCount: z.number().int().min(0).default(0),
  /** When the stored catalogue snapshot was fetched; null for a static list or before the first refresh. */
  catalogueFetchedAt: IsoDateTimeSchema.nullable().default(null),
  /** The coverage is a list committed with the adapter (no refresh). */
  staticCatalogue: z.boolean().default(false),
  /** The last verified webhook delivery received for this provider (null: none yet). */
  lastWebhookAt: IsoDateTimeSchema.nullable().default(null),
  /** Document tries still open at the provider (`creating`, `pending`, `collected`): the poller's work. */
  pendingAttempts: z.number().int().min(0).default(0),
  /** Provider deletions still not confirmed 7 days after they were requested (spec §11 alarm). */
  overdueDeletions: z.number().int().min(0).default(0),
  /**
   * The provider-side retention read back by the last health check of this process (Didit `data_retention_months`;
   * spec §11: unset or above 2 is an alarm); null before a health check or for a provider without one.
   */
  retention: z.object({ months: z.number().nullable(), checkedAt: IsoDateTimeSchema }).nullable().default(null),
})
export type AdminDocumentProvider = z.infer<typeof AdminDocumentProviderSchema>

export const AdminProvidersResponseSchema = z.object({
  biometric: z.array(AdminBiometricProviderSchema),
  ipintel: z.array(AdminIpSourceSchema),
  /** Identity-document providers (empty until one is registered). */
  documents: z.array(AdminDocumentProviderSchema).default([]),
  /** The provider `/join` follows. */
  defaultId: z.string(),
  /** Location v2: the policy in force (Admin → Providers → "Location policy"). */
  locationPolicy: LocationPolicySchema,
})
export type AdminProvidersResponse = z.infer<typeof AdminProvidersResponseSchema>

export const ProviderPathSchema = z.object({ kind: ProviderKindSchema, id: ProviderIdSchema })
export const ProviderIdPathSchema = z.object({ id: ProviderIdSchema })

export const ProviderPatchBodySchema = z.strictObject({
  enabled: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  /** Partial: merged over the stored settings, then validated by the provider's configSchema. */
  settings: ProviderSettingsValuesSchema.optional(),
})
export type ProviderPatchBody = z.infer<typeof ProviderPatchBodySchema>

export const ProviderHealthResponseSchema = z.object({
  ok: z.boolean(),
  detail: z.string(),
  /** Document providers with a retention setting (Didit): the months read back (null: unset = unlimited). */
  retentionMonths: z.number().nullable().optional(),
  credits: z
    .object({ credit: z.number().optional(), quota: z.number().optional(), balanceUsd: z.number().optional() })
    .optional(),
})
export type ProviderHealthResponse = z.infer<typeof ProviderHealthResponseSchema>

export const ProviderSetupResponseSchema = z.object({ ok: z.boolean(), detail: z.string() })

export const IpTestBodySchema = z.strictObject({
  ip: z.string().trim().min(2).max(64),
  placeId: z.number().int().positive().optional(),
})
export const IpTestResponseSchema = z.object({
  ip: z.string(),
  /** Each effective source's normalised answer, keyed by source id. */
  results: z.record(z.string(), z.looseObject({ ok: z.boolean(), source: z.string() })),
  /** With a place: that place, and the IP alone evaluated against it under the policy in force (no device fix). */
  place: z
    .object({ id: z.number().int(), name: z.string(), admin1: z.string().nullable(), country: z.string() })
    .nullable(),
  evaluation: z
    .object({
      ip: IpEvaluationSchema,
      /** The IP-only reasons (ip_country_mismatch, vpn_or_proxy). */
      reasons: z.array(z.string()),
    })
    .nullable(),
})
export type IpTestResponse = z.infer<typeof IpTestResponseSchema>

/* ------------------------------------------------------------------ public */

export const PublicJoinParamsSchema = z.object({ providerId: ProviderIdSchema })

export const PublicJoinResponseSchema = z.object({
  /** false: the page shows "This registration link is not active. Please use <defaultJoinPath>". */
  active: z.boolean(),
  providerId: z.string(),
  displayName: z.string(),
  flow: ProviderFlowSchema.nullable(),
  desktop: z.boolean().nullable(),
  reviewMedia: z.boolean().nullable(),
  faceNotice: FaceNoticeSchema.nullable(),
  erasureNotice: ErasureNoticeSchema.nullable(),
  /** Non-secret settings the member pages need before a start (empty while not active). */
  publicSettings: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
  defaultJoinPath: z.string(),
})
export type PublicJoinResponse = z.infer<typeof PublicJoinResponseSchema>

export const RecipientSchema = z.object({
  id: z.string(),
  kind: ProviderKindSchema,
  legalName: z.string(),
  purpose: z.string(),
  region: z.string(),
})
export type Recipient = z.infer<typeof RecipientSchema>

export const RecipientsResponseSchema = z.object({
  /** Effective external providers and sources. */
  current: z.array(RecipientSchema),
  /** No longer effective but may still hold member data (P4). */
  past: z.array(RecipientSchema),
  /** Local data sets and header readers: they receive nothing. */
  dataSources: z.array(z.object({ id: z.string().nullable(), name: z.string(), purpose: z.string() })),
  /** Fixed processors of the service itself. */
  processors: z.array(z.object({ legalName: z.string(), purpose: z.string(), region: z.string() })),
})
export type RecipientsResponse = z.infer<typeof RecipientsResponseSchema>

export const providersRoutes = {
  adminProviders: route({
    method: 'GET',
    path: '/api/admin/providers',
    auth: 'admin',
    response: AdminProvidersResponseSchema,
  }),
  adminProviderUpdate: route({
    method: 'PATCH',
    path: '/api/admin/providers/:kind/:id',
    auth: 'admin',
    params: ProviderPathSchema,
    body: ProviderPatchBodySchema,
    response: AdminProvidersResponseSchema,
  }),
  adminProviderSetup: route({
    method: 'POST',
    path: '/api/admin/providers/biometric/:id/setup',
    auth: 'admin',
    params: ProviderIdPathSchema,
    response: ProviderSetupResponseSchema,
  }),
  adminProviderHealth: route({
    method: 'GET',
    path: '/api/admin/providers/:kind/:id/health',
    auth: 'admin',
    params: ProviderPathSchema,
    response: ProviderHealthResponseSchema,
  }),
  adminLocationPolicyUpdate: route({
    method: 'PATCH',
    path: '/api/admin/location-policy',
    auth: 'admin',
    body: LocationPolicyPatchSchema,
    response: AdminProvidersResponseSchema,
  }),
  adminIpTest: route({
    method: 'POST',
    path: '/api/admin/ipintel/test',
    auth: 'admin',
    body: IpTestBodySchema,
    response: IpTestResponseSchema,
  }),
  publicJoinDefault: route({
    method: 'GET',
    path: '/api/public/join',
    auth: 'public',
    response: PublicJoinResponseSchema,
  }),
  publicJoin: route({
    method: 'GET',
    path: '/api/public/join/:providerId',
    auth: 'public',
    params: PublicJoinParamsSchema,
    response: PublicJoinResponseSchema,
  }),
  publicRecipients: route({
    method: 'GET',
    path: '/api/public/recipients',
    auth: 'public',
    response: RecipientsResponseSchema,
  }),
}
